import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts';
import { formatCurrency, CHART_COLORS } from '../../utils/telstraUtils';
import type { TelstraMobileReport } from '../../services/telstraService';

/**
 * Calculate device type breakdown from report
 */
export function getDeviceBreakdown(report: TelstraMobileReport | null) {
  const buckets: Record<string, { lines: number; billTotal: number; monthlyFees: number; dataMb: number }> = {};
  
  if (report?.rows?.length) {
    for (const r of report.rows) {
      const key = (r.deviceType || 'UNKNOWN TYPE').toUpperCase();
      if (!buckets[key]) {
        buckets[key] = { lines: 0, billTotal: 0, monthlyFees: 0, dataMb: 0 };
      }
      buckets[key].lines += 1;
      buckets[key].billTotal += Number(r.latestMonthBillTotal || 0);
      buckets[key].monthlyFees += Number(r.monthlyFee || 0);
      buckets[key].dataMb += Number(r.latestMonthDataMb || 0);
    }
  }
  
  const totalBills = Object.values(buckets).reduce((sum, b) => sum + b.billTotal, 0);
  const items = Object.entries(buckets).sort((a, b) => b[1].billTotal - a[1].billTotal);
  return { items, totalBills };
}

interface DeviceTypePieChartProps {
  report: TelstraMobileReport | null;
}

/**
 * Pie chart showing cost breakdown by device type
 */
export function DeviceTypePieChart({ report }: DeviceTypePieChartProps) {
  const { items, totalBills } = getDeviceBreakdown(report);
  
  const chartData: Array<{ name: string; value: number; lines: number; color: string; [key: string]: string | number }> = items.slice(0, 8).map(([name, bucket], index) => ({
    name: name.charAt(0) + name.slice(1).toLowerCase(),
    value: bucket.billTotal,
    lines: bucket.lines,
    color: CHART_COLORS.primary[index % CHART_COLORS.primary.length]
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = totalBills > 0 ? ((data.value / totalBills) * 100).toFixed(1) : '0';
      return (
        <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-neutral-900">{data.name}</p>
          <p className="text-sm text-neutral-600">{formatCurrency(data.value)}</p>
          <p className="text-xs text-neutral-500">{data.lines} lines ({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-neutral-400">
        No data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={2}
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.color || CHART_COLORS.primary[index % CHART_COLORS.primary.length]} 
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          formatter={(value) => <span className="text-xs text-neutral-600">{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

interface TopCostBarChartProps {
  report: TelstraMobileReport | null;
  limit?: number;
  title?: string;
}

/**
 * Bar chart showing top cost items
 */
export function TopCostBarChart({ report, limit = 10, title }: TopCostBarChartProps) {
  const chartData = (report?.rows || [])
    .sort((a, b) => (b.latestMonthBillTotal || 0) - (a.latestMonthBillTotal || 0))
    .slice(0, limit)
    .map(r => ({
      name: r.userName || r.mobileNumber.slice(-4),
      cost: r.latestMonthBillTotal || 0,
      fullName: r.userName || r.mobileNumber,
      plan: r.planName
    }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-neutral-900">{data.fullName}</p>
          <p className="text-sm text-primary-600">{formatCurrency(data.cost)}</p>
          {data.plan && <p className="text-xs text-neutral-500">{data.plan}</p>}
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-neutral-400">
        No data available
      </div>
    );
  }

  return (
    <div>
      {title && <h4 className="text-sm font-medium text-neutral-700 mb-3">{title}</h4>}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis 
            type="number" 
            tickFormatter={(value) => `$${value}`}
            fontSize={12}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={80}
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="cost" 
            fill="#3b82f6" 
            radius={[0, 4, 4, 0]}
            maxBarSize={24}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface DataUsageChartProps {
  report: TelstraMobileReport | null;
  limit?: number;
}

/**
 * Bar chart showing data usage by user
 */
export function DataUsageChart({ report, limit = 10 }: DataUsageChartProps) {
  const chartData = (report?.rows || [])
    .filter(r => (r.latestMonthDataMb || 0) > 0)
    .sort((a, b) => (b.latestMonthDataMb || 0) - (a.latestMonthDataMb || 0))
    .slice(0, limit)
    .map(r => ({
      name: r.userName || r.mobileNumber.slice(-4),
      data: Math.round((r.latestMonthDataMb || 0) / 1024 * 10) / 10, // Convert to GB
      fullName: r.userName || r.mobileNumber,
      allowance: r.includedData ? Math.round(r.includedData / 1024 * 10) / 10 : 0
    }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-neutral-900">{data.fullName}</p>
          <p className="text-sm text-blue-600">{data.data} GB used</p>
          {data.allowance > 0 && (
            <p className="text-xs text-neutral-500">of {data.allowance} GB allowance</p>
          )}
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-neutral-400">
        No data usage available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
        <XAxis 
          type="number" 
          tickFormatter={(value) => `${value}GB`}
          fontSize={12}
        />
        <YAxis 
          type="category" 
          dataKey="name" 
          width={80}
          fontSize={12}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar 
          dataKey="data" 
          fill="#06b6d4" 
          radius={[0, 4, 4, 0]}
          maxBarSize={24}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

interface PlanDistributionChartProps {
  report: TelstraMobileReport | null;
}

/**
 * Pie chart showing plan distribution
 */
export function PlanDistributionChart({ report }: PlanDistributionChartProps) {
  const planCounts: Record<string, { count: number; cost: number }> = {};
  
  if (report?.rows?.length) {
    for (const r of report.rows) {
      const plan = r.planName || 'Unknown Plan';
      if (!planCounts[plan]) {
        planCounts[plan] = { count: 0, cost: 0 };
      }
      planCounts[plan].count += 1;
      planCounts[plan].cost += r.latestMonthBillTotal || 0;
    }
  }

  const chartData: Array<{ name: string; fullName: string; value: number; cost: number; color: string; [key: string]: string | number }> = Object.entries(planCounts)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 8)
    .map(([name, data], index) => ({
      name: name.length > 20 ? name.slice(0, 20) + '...' : name,
      fullName: name,
      value: data.count,
      cost: data.cost,
      color: CHART_COLORS.primary[index % CHART_COLORS.primary.length]
    }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-neutral-900">{data.fullName}</p>
          <p className="text-sm text-neutral-600">{data.value} lines</p>
          <p className="text-xs text-neutral-500">{formatCurrency(data.cost)} total</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-neutral-400">
        No plan data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          outerRadius={100}
          paddingAngle={2}
          dataKey="value"
          label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
          labelLine={false}
        >
          {chartData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.color || CHART_COLORS.primary[index % CHART_COLORS.primary.length]} 
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
}

interface QuickStatsProps {
  report: TelstraMobileReport | null;
}

/**
 * Quick statistics cards
 */
export function QuickStats({ report }: QuickStatsProps) {
  if (!report) return null;
  
  const lines = report.rows?.length || 0;
  const totalBills = report.totals?.totalLatestMonthBills || 0;
  const totalFees = report.totals?.totalMonthlyFees || 0;
  const totalData = report.totals?.totalLatestMonthDataMb || 0;

  const avgBillPerLine = lines > 0 ? totalBills / lines : 0;
  const avgDataPerLine = lines > 0 ? totalData / lines : 0;
  const feeVsBill = totalBills - totalFees;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <div className="text-sm text-neutral-600 mb-1">Avg Bill per Line</div>
        <div className="text-2xl font-bold text-neutral-900">{formatCurrency(avgBillPerLine)}</div>
      </div>
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <div className="text-sm text-neutral-600 mb-1">Avg Data per Line</div>
        <div className="text-2xl font-bold text-neutral-900">
          {Math.round(avgDataPerLine / 1024 * 10) / 10} GB
        </div>
      </div>
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <div className="text-sm text-neutral-600 mb-1">Fee vs Actual Bill</div>
        <div className={`text-2xl font-bold ${feeVsBill > 0 ? 'text-red-600' : 'text-green-600'}`}>
          {feeVsBill > 0 ? '+' : ''}{formatCurrency(feeVsBill)}
        </div>
        <div className="text-xs text-neutral-500">{feeVsBill > 0 ? 'over budget' : 'under budget'}</div>
      </div>
    </div>
  );
}

